// React functional component that adds an observation to a farm, an area, a line, or a point.
// The view will render in a material UI Dialog

import React, { useState } from "react"
import { gql, useMutation } from "@apollo/client"
import { ADD_OBSERVATION } from "../../gql/observations"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@mui/material"

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import dayjs from "dayjs"
import { payloadVersions } from "./observationVersionConstants"

const FILE_UPLOAD_MUTATION = gql`
  mutation FileUpload(
    $base64str: String!
    $file_name: String!
    $file_type: String!
    $user_id: Int!
  ) {
    fileUpload(
      base64str: $base64str
      user_id: $user_id
      file_name: $file_name
      file_type: $file_type
    ) {
      file_path
      file_id
    }
  }
`

const ADD_FILE_TO_OBSERVATION = gql`
  mutation addFileToObservation($file_id: Int!, $observation_id: Int!) {
    insert_observation_attachment(
      objects: { file_id: $file_id, observation_id: $observation_id }
    ) {
      returning {
        id
        file_id
      }
    }
  }
`

export default function AddObservation({
  locationId,
  observationVersion,
  open,
  setOpen,
}) {
  const payloadInfo = payloadVersions.find(
    (p) => p.payloadVersion === observationVersion
  )
  const [addObservation] = useMutation(ADD_OBSERVATION)
  const [file, setFile] = useState(null)
  const [base64Str, setBase64Str] = useState(null)
  const [fileUploadMutation] = useMutation(FILE_UPLOAD_MUTATION)
  const [addFileToObservationMutation] = useMutation(ADD_FILE_TO_OBSERVATION)

  // add the default values for the fields
  const [observation, setObservation] = React.useState({
    date: new dayjs(),
    ...payloadInfo.fields.reduce(
      (acc, cur) => ({
        [cur.name]: cur.default,
        ...acc,
      }),
      []
    ),
  })

  const handleSaveObservation = async () => {
    const addObservationRes = await addObservation({
      variables: {
        data: observation,
        observation_date: "now()",
        location_id: locationId,
        payload_version: observationVersion.toString(),
      },
    })
  // Observation created, proceed to attach file if provided
    if (file && addObservationRes?.data?.insert_observation_one?.id) {
  // Uploading attached file
      const fileUploadRes = await fileUploadMutation({
        variables: {
          file_name: file.name,
          file_type: file.type,
          base64str: base64Str,
          user_id: 1,
        },
      })
  // Received file upload response
      const uploadedFileId = fileUploadRes?.data?.fileUpload?.file_id || null
      if (uploadedFileId) {
        await addFileToObservationMutation({
          variables: {
            file_id: uploadedFileId,
            observation_id: addObservationRes.data?.insert_observation_one?.id,
          },
        })
  // File attached to observation
      }
    }
    setOpen(false)
  }

  const handleChange = (e) => {
  setObservation({ ...observation, [e.target.name]: e.target.value })
  }

  const handleDateChange = (newValue) =>
    setObservation({ ...observation, date: newValue })

  const handleFileChange = (e) => {
    setFile(e.target.files[0])
    const reader = new FileReader()
    if (e.target.files[0]) {
      reader.readAsBinaryString(e.target.files[0])
    }
    reader.onload = function () {
  const base64str = window.btoa(reader.result)
      setBase64Str(base64str)
    }
    reader.onerror = function () {
      // ignore file read errors (could add user-facing error handling)
    }
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>Add Observation</DialogTitle>
      <DialogContent>
        {payloadInfo.fields.map((field) => (
          <TextField
            key={field.name}
            autoFocus
            margin="dense"
            name={field.name}
            label={field.description}
            type={field.type}
            fullWidth
            value={observation[field.name]}
            onChange={handleChange}
          />
        ))}
        {/* A material ui date time field */}
        <DateTimePicker
          value={observation.date}
          onChange={handleDateChange}
          label="Observation date time picker"
        />

        <Typography variant="h5">Add file to observation</Typography>
        <input type="file" onChange={handleFileChange} required />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={handleSaveObservation}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}
